import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import CatalogLayout from './components/CatalogLayout';
import AdminPanel from './components/AdminPanel';
import PublicContactForm from './components/PublicContactForm';
import QuickAddContact from './components/QuickAddContact';
import CevadorPage from './pages/CevadorPage';
import CevadorLanding from './pages/CevadorLanding';
import CustomerArea from './pages/CustomerArea';
import { CartProvider, useCart } from './context/CartContext';
import { CustomerProvider } from './context/CustomerContext';
import { getSettings, getCategories, getProducts, getPromoBanners } from './services/api';
import { Toaster } from './components/ui/toaster';
import { Toaster as SonnerToaster } from 'sonner';

// Wrapper component to connect Customer and Cart contexts
const AppWithProviders = ({ children }) => {
  const cartRef = useRef(null);
  
  const handleCustomerLogin = (token) => {
    if (cartRef.current?.onCustomerLogin) {
      cartRef.current.onCustomerLogin(token);
    }
  };
  
  const handleCustomerLogout = () => {
    if (cartRef.current?.onCustomerLogout) {
      cartRef.current.onCustomerLogout();
    }
  };
  
  return (
    <CartProvider>
      <CartContextConnector ref={cartRef}>
        <CustomerProvider onLogin={handleCustomerLogin} onLogout={handleCustomerLogout}>
          {children}
        </CustomerProvider>
      </CartContextConnector>
    </CartProvider>
  );
};

// Helper component to expose cart context methods via ref
const CartContextConnector = React.forwardRef(({ children }, ref) => {
  const cart = useCart();
  
  React.useImperativeHandle(ref, () => ({
    onCustomerLogin: cart.onCustomerLogin,
    onCustomerLogout: cart.onCustomerLogout
  }));
  
  return children;
});

function App() {
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [promoBanners, setPromoBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showCevador, setShowCevador] = useState(false);
  const [showCevadorLanding, setShowCevadorLanding] = useState(false);
  const [showCustomerArea, setShowCustomerArea] = useState(false);

  // Check if URL is /admin or /contato or /quick or /cevador or /cliente or /cevadores
  useEffect(() => {
    const path = window.location.pathname;
    if (path === '/admin') {
      setShowAdmin(true);
    } else if (path === '/contato' || path === '/cadastro') {
      setShowContactForm(true);
    } else if (path === '/quick' || path === '/rapido') {
      setShowQuickAdd(true);
    } else if (path === '/cevador' || path === '/guia-cevadores') {
      setShowCevador(true);
    } else if (path === '/cevadores' || path === '/landing-cevadores') {
      setShowCevadorLanding(true);
    } else if (path === '/cliente' || path === '/minha-conta' || path === '/meus-pedidos') {
      setShowCustomerArea(true);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsData, categoriesData, productsData, bannersData] = await Promise.all([
        getSettings(),
        getCategories(),
        getProducts(),
        getPromoBanners()
      ]);
      setSettings(settingsData);
      setCategories(categoriesData);
      setProducts(productsData);
      setPromoBanners(bannersData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Keyboard shortcut to open admin (Ctrl+Shift+A)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setShowAdmin(true);
        window.history.pushState({}, '', '/admin');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleAdminOpen = () => {
    setShowAdmin(true);
    window.history.pushState({}, '', '/admin');
  };

  const handleAdminClose = () => {
    setShowAdmin(false);
    window.history.pushState({}, '', '/');
    fetchData();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-[#1B93A4] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500 font-medium">Carregando catálogo...</p>
        </div>
      </div>
    );
  }

  // Show public contact form
  if (showContactForm) {
    return (
      <>
        <PublicContactForm />
        <SonnerToaster position="top-right" richColors closeButton />
      </>
    );
  }

  // Show quick add contact form
  if (showQuickAdd) {
    return <QuickAddContact />;
  }

  // Show cevador landing page
  if (showCevadorLanding) {
    return (
      <>
        <CevadorLanding />
        <Toaster />
        <SonnerToaster position="top-right" richColors closeButton />
      </>
    );
  }

  // Show cevador page (guia)
  if (showCevador) {
    return (
      <AppWithProviders>
        <CevadorPage />
        <Toaster />
        <SonnerToaster position="top-right" richColors closeButton />
      </AppWithProviders>
    );
  }

  if (showAdmin) {
    return (
      <>
        <AdminPanel onClose={handleAdminClose} />
        <SonnerToaster position="top-right" richColors closeButton />
      </>
    );
  }

  return (
    <AppWithProviders>
      <CatalogLayout 
        settings={settings}
        categories={categories}
        products={products}
        promoBanners={promoBanners}
        onOpenAdmin={handleAdminOpen}
        showCustomerArea={showCustomerArea}
      />
      <Toaster />
      <SonnerToaster position="top-right" richColors closeButton />
    </AppWithProviders>
  );
}

export default App;
